import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map } from 'rxjs';
import { environment } from '../../environments/environment';

/** One (partner, premium) assignment row. */
export interface PartnerPremiumAccess {
  id: number;
  premiumID: number;
  partnerID: number;
  // Joined display fields (from Partners / PremiumRules) — present on GET only.
  partnerName?: string | null;
  partnerCode?: string | null;
  premiumCode?: string | null;
  coverageType?: string | null;
  // The rule's master policy, shown in the partner's Products tab.
  masterPolicyId?: number | null;
  masterPolicyName?: string | null;
  masterPolicyNumber?: string | null;
  isEnable: boolean;
  createdDate?: string | null;
  createdBy?: number | null;
}

/** Bulk assign — one row created per premium, already-assigned ones skipped. */
export interface AssignRequest {
  partnerID: number;
  premiumIds: number[];
  isEnable: boolean;
}

/** Single insert/update payload. */
export interface PartnerPremiumAccessDto {
  id?: number;
  partnerID: number;
  premiumID: number;
  isEnable: boolean;
}

/** Normalize a possibly PascalCase row to the camelCase model. */
function mapRow(x: any): PartnerPremiumAccess {
  return {
    id: x.Id ?? x.id,
    premiumID: x.PremiumID ?? x.premiumID,
    partnerID: x.PartnerID ?? x.partnerID,
    partnerName: x.PartnerName ?? x.partnerName ?? null,
    partnerCode: x.PartnerCode ?? x.partnerCode ?? null,
    premiumCode: x.PremiumCode ?? x.premiumCode ?? null,
    coverageType: x.CoverageType ?? x.coverageType ?? null,
    masterPolicyId: x.MasterPolicyId ?? x.masterPolicyId ?? null,
    masterPolicyName: x.MasterPolicyName ?? x.masterPolicyName ?? null,
    masterPolicyNumber: x.MasterPolicyNumber ?? x.masterPolicyNumber ?? null,
    isEnable: x.IsEnable ?? x.isEnable ?? true,
    createdDate: x.CreatedDate ?? x.createdDate ?? null,
    createdBy: x.CreatedBy ?? x.createdBy ?? null,
  };
}

function mapEnvelope(res: any) {
  if (Array.isArray(res?.data)) return { ...res, data: res.data.map(mapRow) };
  if (res?.data && typeof res.data === 'object' && 'id' in res.data)
    return { ...res, data: mapRow(res.data) };
  if (Array.isArray(res)) return res.map(mapRow);
  return res; // leave Assign's { assigned, skipped } payload untouched
}

@Injectable({ providedIn: 'root' })
export class PartnerPremiumAccessService {
  private http = inject(HttpClient);
  private readonly baseUrl = `${environment.apiUrl}/PartnerPremiumAccess`;

  getAll() {
    return this.http.get(`${this.baseUrl}/GetAll`).pipe(map(mapEnvelope));
  }

  getById(id: number) {
    return this.http.get(`${this.baseUrl}/GetById`, { params: { id } }).pipe(map(mapEnvelope));
  }

  /** Plans this partner is ELIGIBLE for — client-owned master policies are excluded server-side. */
  assignable(partnerId: number) {
    return this.http.get(`${this.baseUrl}/Assignable`, { params: { partnerId } });
  }

  getByPartner(partnerId: number) {
    return this.http
      .get(`${this.baseUrl}/GetByPartner`, { params: { partnerId } })
      .pipe(map(mapEnvelope));
  }

  /** Recommended for the UI: assign many premiums to a partner at once. */
  assign(body: AssignRequest) {
    return this.http.post(`${this.baseUrl}/Assign`, body);
  }

  insert(dto: PartnerPremiumAccessDto) {
    return this.http.post(`${this.baseUrl}/Insert`, dto);
  }

  update(dto: PartnerPremiumAccessDto) {
    return this.http.post(`${this.baseUrl}/Update`, dto);
  }

  delete(id: number) {
    return this.http.get(`${this.baseUrl}/Delete`, { params: { id } });
  }
}
